if (!customElements.get('scroll-rank-trust')) {
  customElements.define(
    'scroll-rank-trust',
    class ScrollRankTrust extends HTMLElement {
      constructor() {
        super();
        this.rafId = null;
        this.isVisible = false;
        this.isManuallyPaused = false;
        this.handleScroll = this.handleScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
        this.handleMediaToggle = this.handleMediaToggle.bind(this);
      }

      connectedCallback() {
        this.stage = this.querySelector('.scroll-rank-trust__stage');
        this.toggle = this.querySelector('[data-scroll-story-media-toggle]');
        this.videos = Array.from(this.querySelectorAll('video'));
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.isManuallyPaused = this.dataset.autoplay !== 'true';

        if (!this.stage) return;

        this.updateToggleState();
        this.toggle?.addEventListener('click', this.handleMediaToggle);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            this.isVisible = entries[0]?.isIntersecting || false;
            this.syncVideoPlayback();
            if (this.isVisible) this.requestUpdate();
          },
          { rootMargin: '25% 0px 25% 0px' }
        );
        this.visibilityObserver.observe(this);

        this.updateProgress();
        this.syncVideoPlayback();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.handleMediaToggle);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        this.visibilityObserver?.disconnect();
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      handleScroll() {
        if (this.isVisible) this.requestUpdate();
      }

      handleResize() {
        this.requestUpdate();
        this.syncVideoPlayback();
      }

      requestUpdate() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.updateProgress();
        });
      }

      updateProgress() {
        if (this.reducedMotion?.matches) return;

        const rect = this.getBoundingClientRect();
        const stageHeight = this.stage.getBoundingClientRect().height;
        const scrollDistance = Math.max(rect.height - stageHeight, 1);
        const progress = this.clamp(-rect.top / scrollDistance);
        const reveal = this.easeInOut(this.range(progress, 0.04, 0.62));
        const detailReveal = this.easeOut(this.range(progress, 0.66, 0.9));
        const rankOpacity = this.easeOut(this.range(progress, 0.1, 0.48));
        const rankScale = 1 + 0.75 * Math.pow(1 - reveal, 2);
        const detailShift = Math.round((1 - detailReveal) * 28);

        this.style.setProperty('--scroll-story-progress', progress.toFixed(4));
        this.style.setProperty('--scroll-story-reveal', reveal.toFixed(4));
        this.style.setProperty('--scroll-story-detail-reveal', detailReveal.toFixed(4));
        this.style.setProperty('--scroll-story-panel-opacity', reveal.toFixed(4));
        this.style.setProperty('--scroll-story-rank-opacity', rankOpacity.toFixed(4));
        this.style.setProperty('--scroll-story-rank-scale', rankScale.toFixed(4));
        this.style.setProperty('--scroll-story-detail-shift', `${detailShift}px`);
      }

      handleMediaToggle() {
        this.isManuallyPaused = !this.isManuallyPaused;
        this.updateToggleState();
        this.syncVideoPlayback();
      }

      updateToggleState() {
        if (!this.toggle) return;
        this.toggle.setAttribute('aria-pressed', String(this.isManuallyPaused));
        this.toggle.setAttribute(
          'aria-label',
          this.isManuallyPaused ? this.dataset.playLabel : this.dataset.pauseLabel
        );
      }

      syncVideoPlayback() {
        if (!this.videos.length) return;

        const shouldPlay = this.isVisible && !this.isManuallyPaused;

        this.videos.forEach((video) => {
          if (!this.isVisibleMedia(video)) {
            video.pause();
            return;
          }

          if (shouldPlay) {
            const playPromise = video.play();
            if (playPromise?.catch) playPromise.catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      isVisibleMedia(video) {
        const panel = video.closest('.scroll-rank-trust__media-panel');
        if (!panel) return true;
        return window.getComputedStyle(panel).display !== 'none';
      }

      range(value, start, end) {
        return this.clamp((value - start) / (end - start));
      }

      clamp(value) {
        return Math.min(Math.max(value, 0), 1);
      }

      easeInOut(value) {
        return value < 0.5
          ? 4 * value * value * value
          : 1 - Math.pow(-2 * value + 2, 3) / 2;
      }

      easeOut(value) {
        return 1 - Math.pow(1 - value, 3);
      }
    }
  );
}
